import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, ApiError } from '../api.ts'
import { dueLabel, taskTypeLabel } from '../format.ts'

interface Task {
  id: string
  kind: string
  title: string
  dueAt: string | null
  status: string
  assigneeCode: string | null
  customerId: string | null
  customerName: string | null
  quoteId: string | null
  unitId: string | null
  serviceTicketId: string | null
}

export function TasksPage() {
  const [tasks, setTasks] = useState<Task[]>([])
  const [scope, setScope] = useState<'moje' | 'wszystkie'>('moje')
  const [newTitle, setNewTitle] = useState('')
  const [newDue, setNewDue] = useState('')
  const [error, setError] = useState<string | null>(null)

  function reload() {
    api.get<{ tasks: Task[] }>(`/api/tasks${scope === 'moje' ? '?scope=moje' : ''}`).then((r) => setTasks(r.tasks)).catch(() => {})
  }
  useEffect(reload, [scope])

  async function complete(id: string) {
    await api.post(`/api/tasks/${id}/complete`).catch(() => {})
    reload()
  }
  async function claim(id: string) {
    await api.post(`/api/tasks/${id}/claim`).catch(() => {})
    reload()
  }
  async function add() {
    if (!newTitle.trim()) return
    setError(null)
    try {
      await api.post('/api/tasks', { title: newTitle.trim(), dueAt: newDue ? new Date(newDue).toISOString() : undefined })
      setNewTitle(''); setNewDue('')
      reload()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Błąd')
    }
  }

  function linkFor(t: Task): string | null {
    if (t.quoteId) return `/oferty/${t.quoteId}`
    if (t.serviceTicketId) return `/serwis/${t.serviceTicketId}`
    if (t.unitId) return `/magazyn/${t.unitId}`
    if (t.customerId) return `/klienci/${t.customerId}`
    return null
  }

  return (
    <div className="p-6 max-w-3xl">
      <h1 className="text-xl font-bold text-slate-800 mb-1">Zadania</h1>
      <p className="text-sm text-slate-500 mb-4">Follow-upy, przeglądy gwarancyjne i zadania własne.</p>

      <div className="flex gap-2 mb-3">
        <button onClick={() => setScope('moje')} className={`text-sm px-3 py-1.5 rounded-md ${scope === 'moje' ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-600'}`}>Moje</button>
        <button onClick={() => setScope('wszystkie')} className={`text-sm px-3 py-1.5 rounded-md ${scope === 'wszystkie' ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-600'}`}>Wszystkie</button>
      </div>

      {/* Quick add */}
      <div className="flex gap-2 mb-4">
        <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} placeholder="Nowe zadanie…" className="flex-1 px-3 py-1.5 border border-slate-300 rounded-md text-sm focus:border-brand-600" />
        <input type="date" value={newDue} onChange={(e) => setNewDue(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-md text-sm" />
        <button onClick={() => void add()} className="text-sm bg-slate-100 text-slate-700 px-3 py-1.5 rounded-md hover:bg-slate-200">Dodaj</button>
      </div>
      {error && <div className="text-sm text-red-700 mb-2">{error}</div>}

      <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
        {tasks.map((t) => {
          const due = t.dueAt ? dueLabel(t.dueAt) : null
          const link = linkFor(t)
          return (
            <div key={t.id} className="p-3 flex items-start gap-3">
              <button onClick={() => void complete(t.id)} title="Zrobione" className="mt-0.5 w-4 h-4 rounded border border-slate-300 hover:bg-green-100 hover:border-green-500 shrink-0" />
              <div className="min-w-0 flex-1">
                {link ? (
                  <Link to={link} className="text-sm text-slate-700 hover:text-brand-700">{t.title}</Link>
                ) : (
                  <span className="text-sm text-slate-700">{t.title}</span>
                )}
                <div className="text-xs text-slate-400">
                  {taskTypeLabel(t.kind)}
                  {t.customerName && ` · ${t.customerName}`}
                  {due && <span className={due.overdue ? 'text-red-600' : due.today ? 'text-amber-600' : ''}> · {due.label}</span>}
                  {t.assigneeCode ? ` · ${t.assigneeCode}` : ' · nieprzypisane'}
                </div>
              </div>
              {!t.assigneeCode && (
                <button onClick={() => void claim(t.id)} className="text-xs text-brand-600 hover:underline shrink-0">weź</button>
              )}
            </div>
          )
        })}
        {tasks.length === 0 && <div className="p-6 text-center text-slate-400 text-sm">Brak zadań.</div>}
      </div>
    </div>
  )
}
